import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/Auth";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

const MemberFamilyInformation = ({ familyDetails, memberDetails }) => {
  const { requestWithAuth } = useAuth();
  const [updating, setUpdating] = useState(false);
  const [updateData, setUpdateData] = useState({});
  const [showChildModal, setShowChildModal] = useState(false);
  const [newChild, setNewChild] = useState({
    name: "",
    date_of_birth: "",
    gender: "",
    relationship: "",
  });
  const [addingChild, setAddingChild] = useState(false);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested objects
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setUpdateData({
        ...updateData,
        [parent]: {
          ...updateData[parent],
          [child]: value,
        },
      });
    } else {
      setUpdateData({
        ...updateData,
        [name]: value,
      });
    }
  };

  const handleChildFormChange = (e) => {
    setNewChild({
      ...newChild,
      [e.target.name]: e.target.value,
    });
  };

  useEffect(() => {
    if (familyDetails) {
      setUpdateData(familyDetails);
    }
  }, [familyDetails]);

  const marriageTypes = [
    "Traditional", "Ordinance", "Both Traditional and Ordinance", "Customary", "Court"
  ];

  const relationshipOptions = [
    "Son", "Daughter", "Stepson", "Stepdaughter", "Adopted Son", "Adopted Daughter"
  ];

  const updateFamilyDetails = async (e) => {
    e.preventDefault();
    setUpdating(true);
    try {
      const body = JSON.stringify(updateData);
      await requestWithAuth(
        `/members/${memberDetails.id}/family/`,
        "PUT",
        body,
        {
          "Content-Type": "application/json",
        }
      );
      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Family details updated successfully.",
      }).then(() => {
        window.location.reload();
      });
    } catch (error) {
      console.log(error);
      toast.error("Failed to update family details. Please try again.");
    } finally {
      setUpdating(false);
    }
  };

  const addChild = async (e) => {
    e.preventDefault();
    setAddingChild(true);
    try {
      const body = JSON.stringify({
        ...newChild,
        member_id: memberDetails.id,
      });
      await requestWithAuth(
        `/members/${memberDetails.id}/children/`,
        "POST",
        body,
        {
          "Content-Type": "application/json",
        }
      );
      toast.success("Child added successfully!");
      setShowChildModal(false);
      setNewChild({
        name: "",
        date_of_birth: "",
        gender: "",
        relationship: "",
      });
      // Refresh the page or update the children list
      window.location.reload();
    } catch (error) {
      console.log(error);
      toast.error("Failed to add child. Please try again.");
    } finally {
      setAddingChild(false);
    }
  };

  return (
    <>
      <form onSubmit={updateFamilyDetails} className="border border-gray-300 py-5 px-5">
        {/* Spouse Information Section */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3 text-gray-700">Spouse Information</h3>
          <div className="grid lg:grid-cols-3 gap-3">
            <div className="flex flex-col">
              <label htmlFor="" className="font-semibold">
                Name of spouse
              </label>
              <input
                name="spouse_info.name"
                type="text"
                onChange={handleFormChange}
                value={updateData?.spouse_info?.name || ""}
                className="px-2 py-1 rounded border border-gray-200"
              />
            </div>
            <div className="flex flex-col">
              <label htmlFor="" className="font-semibold">
                Date of marriage
              </label>
              <input
                name="spouse_info.date_of_marriage"
                type="date"
                onChange={handleFormChange}
                value={updateData?.spouse_info?.date_of_marriage || ""}
                className="px-2 py-1 rounded border border-gray-200"
              />
            </div>
            <div className="flex flex-col">
              <label htmlFor="" className="font-semibold">
                Type of marriage
              </label>
              <select
                name="spouse_info.type_of_marriage"
                onChange={handleFormChange}
                value={updateData?.spouse_info?.type_of_marriage || ""}
                className="px-2 py-1 rounded border border-gray-200"
              >
                <option value="">Select marriage type</option>
                {marriageTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col">
              <label htmlFor="" className="font-semibold">
                Church of spouse
              </label>
              <input
                name="spouse_info.spouse_church"
                type="text"
                onChange={handleFormChange}
                value={updateData?.spouse_info?.spouse_church || ""}
                className="px-2 py-1 rounded border border-gray-200"
              />
            </div>
            <div className="flex flex-col">
              <label htmlFor="" className="font-semibold">
                Occupation of spouse
              </label>
              <input
                name="spouse_info.spouse_occupation"
                type="text"
                onChange={handleFormChange}
                value={updateData?.spouse_info?.spouse_occupation || ""}
                className="px-2 py-1 rounded border border-gray-200"
              />
            </div>
            <div className="flex flex-col">
              <label htmlFor="" className="font-semibold">
                Contact of spouse
              </label>
              <input
                name="spouse_info.spouse_contact"
                type="text"
                onChange={handleFormChange}
                value={updateData?.spouse_info?.spouse_contact || ""}
                className="px-2 py-1 rounded border border-gray-200"
              />
            </div>
          </div>
        </div>

        {/* Parents Information Section */}
        <div className="mb-6 border-t border-gray-200 pt-5">
          <h3 className="text-lg font-semibold mb-3 text-gray-700">Parents Information</h3>
          <div className="grid lg:grid-cols-3 gap-3">
            <div className="flex flex-col">
              <label htmlFor="" className="font-semibold">
                Father's name
              </label>
              <input
                name="father_name"
                type="text"
                onChange={handleFormChange}
                value={updateData?.father_name || ""}
                className="px-2 py-1 rounded border border-gray-200"
              />
            </div>
            <div className="flex flex-col">
              <label htmlFor="" className="font-semibold">
                Contact of Father
              </label>
              <input
                name="father_contact"
                type="text"
                onChange={handleFormChange}
                value={updateData?.father_contact || ""}
                className="px-2 py-1 rounded border border-gray-200"
              />
            </div>
            <div className="flex flex-col">
              <label htmlFor="" className="font-semibold">
                Address of father
              </label>
              <input
                name="father_address"
                type="text"
                onChange={handleFormChange}
                value={updateData?.father_address || ""}
                className="px-2 py-1 rounded border border-gray-200"
              />
            </div>
            <div className="flex flex-col">
              <label htmlFor="" className="font-semibold">
                Occupation of Father
              </label>
              <input
                name="father_occupation"
                type="text"
                onChange={handleFormChange}
                value={updateData?.father_occupation || ""}
                className="px-2 py-1 rounded border border-gray-200"
              />
            </div>
            <div className="flex flex-col">
              <label htmlFor="" className="font-semibold">
                Father's Hometown
              </label>
              <input
                name="father_hometown"
                type="text"
                onChange={handleFormChange}
                value={updateData?.father_hometown || ""}
                className="px-2 py-1 rounded border border-gray-200"
              />
            </div>
            <div className="flex flex-col">
              <label htmlFor="" className="font-semibold">
                Father's Nationality
              </label>
              <input
                name="father_nationality"
                type="text"
                onChange={handleFormChange}
                value={updateData?.father_nationality || ""}
                className="px-2 py-1 rounded border border-gray-200"
              />
            </div>
            <div className="flex flex-col">
              <label htmlFor="" className="font-semibold">
                Mother's Name
              </label>
              <input
                name="mother_name"
                type="text"
                onChange={handleFormChange}
                value={updateData?.mother_name || ""}
                className="px-2 py-1 rounded border border-gray-200"
              />
            </div>
            <div className="flex flex-col">
              <label htmlFor="" className="font-semibold">
                Contact of Mother
              </label>
              <input
                name="mother_contact"
                type="text"
                onChange={handleFormChange}
                value={updateData?.mother_contact || ""}
                className="px-2 py-1 rounded border border-gray-200"
              />
            </div>
            <div className="flex flex-col">
              <label htmlFor="" className="font-semibold">
                Address of Mother
              </label>
              <input
                name="mother_address"
                type="text"
                onChange={handleFormChange}
                value={updateData?.mother_address || ""}
                className="px-2 py-1 rounded border border-gray-200"
              />
            </div>
            <div className="flex flex-col">
              <label htmlFor="" className="font-semibold">
                Occupation of Mother
              </label>
              <input
                name="mother_occupation"
                type="text"
                onChange={handleFormChange}
                value={updateData?.mother_occupation || ""}
                className="px-2 py-1 rounded border border-gray-200"
              />
            </div>
            <div className="flex flex-col">
              <label htmlFor="" className="font-semibold">
                Mother's Hometown
              </label>
              <input
                name="mother_hometown"
                type="text"
                onChange={handleFormChange}
                value={updateData?.mother_hometown || ""}
                className="px-2 py-1 rounded border border-gray-200"
              />
            </div>
            <div className="flex flex-col">
              <label htmlFor="" className="font-semibold">
                Mother's Nationality
              </label>
              <input
                name="mother_nationality"
                type="text"
                onChange={handleFormChange}
                value={updateData?.mother_nationality || ""}
                className="px-2 py-1 rounded border border-gray-200"
              />
            </div>
          </div>
        </div>

        {/* Emergency Contacts Section */}
        <div className="mb-6 border-t border-gray-200 pt-5">
          <h3 className="text-lg font-semibold mb-3 text-gray-700">Emergency Contacts</h3>
          <div className="grid lg:grid-cols-3 gap-3">
            <div className="flex flex-col">
              <label htmlFor="" className="font-semibold">
                Next of kin (NOK)
              </label>
              <input
                name="next_of_kin.name"
                type="text"
                onChange={handleFormChange}
                value={updateData?.next_of_kin?.name || ""}
                className="px-2 py-1 rounded border border-gray-200"
              />
            </div>
            <div className="flex flex-col">
              <label htmlFor="" className="font-semibold">
                Relationship to NOK
              </label>
              <input
                name="next_of_kin.relation"
                type="text"
                onChange={handleFormChange}
                value={updateData?.next_of_kin?.relation || ""}
                className="px-2 py-1 rounded border border-gray-200"
              />
            </div>
            <div className="flex flex-col">
              <label htmlFor="" className="font-semibold">
                Address of NOK
              </label>
              <input
                name="next_of_kin.address"
                type="text"
                onChange={handleFormChange}
                value={updateData?.next_of_kin?.address || ""}
                className="px-2 py-1 rounded border border-gray-200"
              />
            </div>
            <div className="flex flex-col">
              <label htmlFor="" className="font-semibold">
                Contact of NOK
              </label>
              <input
                name="next_of_kin.contact"
                type="text"
                onChange={handleFormChange}
                value={updateData?.next_of_kin?.contact || ""}
                className="px-2 py-1 rounded border border-gray-200"
              />
            </div>
            <div className="flex flex-col">
              <label htmlFor="" className="font-semibold">
                Emergency Contact Person (ECP)
              </label>
              <input
                name="emergency_contact.name"
                type="text"
                onChange={handleFormChange}
                value={updateData?.emergency_contact?.name || ""}
                className="px-2 py-1 rounded border border-gray-200"
              />
            </div>
            <div className="flex flex-col">
              <label htmlFor="" className="font-semibold">
                Relationship to ECP
              </label>
              <input
                name="emergency_contact.relationship"
                type="text"
                onChange={handleFormChange}
                value={updateData?.emergency_contact?.relationship || ""}
                className="px-2 py-1 rounded border border-gray-200"
              />
            </div>
            <div className="flex flex-col">
              <label htmlFor="" className="font-semibold">
                Address of ECP
              </label>
              <input
                name="emergency_contact.address"
                type="text"
                onChange={handleFormChange}
                value={updateData?.emergency_contact?.address || ""}
                className="px-2 py-1 rounded border border-gray-200"
              />
            </div>
            <div className="flex flex-col">
              <label htmlFor="" className="font-semibold">
                Contact of ECP
              </label>
              <input
                name="emergency_contact.contact"
                type="text"
                onChange={handleFormChange}
                value={updateData?.emergency_contact?.contact || ""}
                className="px-2 py-1 rounded border border-gray-200"
              />
            </div>
          </div>
        </div>

        {/* Children Section */}
        <div className="border-t border-gray-200 pt-5">
          <div className="flex justify-between items-center py-2 mb-4">
            <h3 className="text-lg font-semibold text-gray-700">Children</h3>
{/*             
            <button
              type="button"
              onClick={() => setShowChildModal(true)}
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            >
              Add Child
            </button> */}
          </div>
          <div className="overflow-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="w-screen">
                <tr className="bg-slate-100 border border-slate-200">
                  <th className="py-2 px-5">Name of child</th>
                  <th className="py-2 px-5">Date of birth</th>
                  <th className="py-2 px-5">Gender</th>
                  <th className="py-2 px-5">Relationship</th>
                </tr>
              </thead>
              <tbody>
                {familyDetails?.children?.length > 0 ? (
                  familyDetails.children.map((child, index) => (
                    <tr className="border-b border-slate-200" key={index}>
                      <td className="px-6 py-2">{child?.name}</td>
                      <td className="px-6 py-2">{child?.date_of_birth}</td>
                      <td className="px-6 py-2">{child?.gender}</td>
                      <td className="px-6 py-2">{child?.relationship}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="px-6 py-2 text-center">
                      No children records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex items-center justify-end mt-6">
          {/* <button
            type="submit"
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors duration-200"
            disabled={updating}
          >
            {updating ? "Updating..." : "Update Family Details"}
          </button> */}
        </div>
      </form>

      {/* Add Child Modal */}
      {showChildModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Add Child</h3>
              <button
                onClick={() => setShowChildModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <form onSubmit={addChild}>
              <div className="space-y-4">
                <div className="flex flex-col">
                  <label className="font-semibold mb-1">Name *</label>
                  <input
                    name="name"
                    type="text"
                    onChange={handleChildFormChange}
                    value={newChild.name}
                    required
                    className="px-2 py-1 rounded border border-gray-200"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="font-semibold mb-1">Date of Birth *</label>
                  <input
                    name="date_of_birth"
                    type="date"
                    onChange={handleChildFormChange}
                    value={newChild.date_of_birth}
                    required
                    className="px-2 py-1 rounded border border-gray-200"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="font-semibold mb-1">Gender *</label>
                  <select
                    name="gender"
                    onChange={handleChildFormChange}
                    value={newChild.gender}
                    required
                    className="px-2 py-1 rounded border border-gray-200"
                  >
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
                <div className="flex flex-col">
                  <label className="font-semibold mb-1">Relationship *</label>
                  <select
                    name="relationship"
                    onChange={handleChildFormChange}
                    value={newChild.relationship}
                    required
                    className="px-2 py-1 rounded border border-gray-200"
                  >
                    <option value="">Select relationship</option>
                    {relationshipOptions.map((relation) => (
                      <option key={relation} value={relation}>
                        {relation}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setShowChildModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  disabled={addingChild}
                >
                  {addingChild ? "Adding..." : "Add Child"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default MemberFamilyInformation;